/**
 * The gameplay feature host contract (plan §2.4). FROZEN for G7-G14 once committed: features code against
 * exactly these names. Additive changes only.
 *
 * A feature lives in `src/features/<name>/feature.ts` and default-exports a `GameFeature`. The app discovers
 * it with `import.meta.glob` (src/app/features.ts), so feature tasks never touch `main.ts` or `src/app/**`.
 *
 * Frame order (plan §2.4): input → every `beforeTicks` (M2) → sim ticks (each followed by every `afterTick`;
 * none while a hold is active) → interpolation → camera rig → ship views → every `frame` in `order` →
 * `lib.update`, `env.update` → `post.render()`.
 * `beforeTicks`, `afterTick` and `frame` run in the hot loop: they must not allocate (D-014).
 *
 * M2 additions (M2 plan §2.4, additive): the game mode, holds, the typed service registry, the asset loader for
 * deferred assets, the start override, `FrameInfo.held`, `GameFeature.beforeTicks` and six `FEATURE_ORDER` entries.
 */
import type { PerspectiveCamera, Scene, WebGPURenderer } from 'three/webgpu'
import type { AssetLoader, LoadedModel, LoadedTextureSet } from '../assets/loader'
import type { GameData } from '../data'
import type { AssetsFile } from '../data/assetSchema'
import type { InputDevices } from '../input/devices'
import type { PlayerControl } from '../input/playerInput'
import type { ReachRuntime } from '../reach/runtime/types'
import type { CameraRig } from '../render/cameraRig'
import type { QualityPreset } from '../render/contracts'
import type { Environment } from '../render/environment'
import type { MaterialLibrary } from '../render/materials'
import type { PostPipeline } from '../render/post'
import type { ShipViews } from '../render/shipViews'
import type { Sim } from '../sim'
import type { AimAssistLevel } from '../data/schemas'
import type { HudLabelMode } from '../hud/reach/config'
import type { AwayReport } from '../strategic/types'
import type { Services } from './services'

/**
 * How the game runs (`?mode=career|sandbox|reach`, else `app.json defaultMode`). `career`: the M2 working life (no
 * M1 waves, no M1 auto-respawn; the career feature drives the world). `sandbox`: M1 exactly (waves, respawn).
 * `reach` (M3 R1, plan §2.3): the galaxy (`ctx.reach` non-null) with M3's stand-in career; opt-in through
 * `?mode=reach` until it is finished (wave 2 landing on main).
 *
 * The M2 bridge (R22d, M3 plan §1.2.1, §7 item 11) unifies `reach` and `career` behind `app.json careerInReach`:
 * when it is on, the career runs in the Reach (`ctx.reach` non-null, M2's career and encounters behind the ports)
 * and `?mode=reach` parses to `career`, so `ctx.mode` is never `reach`. Code that belongs to the Reach therefore
 * tests `ctx.reach !== null`, not the mode. `reach` stays in the list (frozen contract, additive only) until the
 * switch is on for good.
 */
export const GAME_MODES = ['career', 'sandbox', 'reach'] as const
export type GameMode = (typeof GAME_MODES)[number]

/** Settings › Interface (spec 2026-10-09 §5.6): text contrast. `high` toggles the `ui-high-contrast` class on <html>. */
export const TEXT_CONTRASTS = ['standard', 'high'] as const
export type TextContrast = (typeof TEXT_CONTRASTS)[number]
/** Reduced motion: `system` follows the OS `prefers-reduced-motion`; on toggles the `ui-reduced-motion` class on <html>. */
export const REDUCED_MOTION_MODES = ['system', 'off', 'on'] as const
export type ReducedMotionMode = (typeof REDUCED_MOTION_MODES)[number]
/** Dashboard time (spec §1.3, D-TBD-3): `pause` holds the sim while the dashboard is open; `keepRunning` does not. */
export const DASHBOARD_TIME_MODES = ['pause', 'keepRunning'] as const
export type DashboardTimeMode = (typeof DASHBOARD_TIME_MODES)[number]

/** Player settings (spec §10). G12 persists them; features read them through `ctx.settings`. */
export interface Settings {
  /** Multiplier on `bindings.mouse.sensitivity` (1 = the bindings default). */
  mouseSensitivity: number
  invertY: boolean
  /** Flight assist state at spawn and respawn. */
  defaultFA: boolean
  quality: QualityPreset
  /** 0..1. */
  masterVolume: number
  screenShake: boolean
  /** Bus volumes 0..1 (audio-direction §6; added by the audio lane, D-044). */
  musicVolume: number
  sfxVolume: number
  uiVolume: number
  commsVolume: number
  /** Hull effect intensity 0..1: how much of your own ship reaches you through the hull (low end, cockpit ring). */
  hullEffect: number
  /** Night mode: dynamic-range compression of the whole mix for quiet listening. */
  nightMode: boolean
  /** Fold the mix to mono. */
  mono: boolean
  /** Aim assist strength (weapons.json `aimAssist.levels`; D-247). */
  aimAssist: AimAssistLevel
  /** Settings › Interface (spec 2026-10-09 §5.6, card F2), additive. UI scale as a fraction (0.75..2, 1 = 100 %), set as `--ui-user-scale` on <html>. */
  uiScale: number
  textContrast: TextContrast
  reducedMotion: ReducedMotionMode
  /** HUD labels (H2): Full / Reduced / Minimal full-label caps (reach-hud.json `labels.caps`). */
  hudLabels: HudLabelMode
  /** Whether the flight dashboard holds the sim (read by the dashboard feature, card D1). */
  dashboardTime: DashboardTimeMode
  /** Hold to confirm destructive actions (sell all, jettison, abandon claim). */
  holdToConfirm: boolean
}

export const DEFAULT_SETTINGS: Readonly<Settings> = Object.freeze({
  mouseSensitivity: 1,
  invertY: false,
  defaultFA: true,
  quality: 'high',
  masterVolume: 0.8,
  screenShake: true,
  musicVolume: 0.7,
  sfxVolume: 1,
  uiVolume: 0.8,
  commsVolume: 1,
  hullEffect: 1,
  nightMode: false,
  mono: false,
  aimAssist: 'standard',
  uiScale: 1,
  textContrast: 'standard',
  reducedMotion: 'system',
  hudLabels: 'reduced',
  dashboardTime: 'pause',
  holdToConfirm: false,
})

/** Why the game paused. The menu feature words its overlay from it (spec §12). */
export type PauseReason = 'user' | 'hidden' | 'blur' | 'pointerLock' | 'gamepad'

/** Bus events and their payloads. */
export interface GameBusEvents {
  /** The game just paused (the loop already stopped ticking and released pointer lock). */
  pause: PauseReason
  /**
   * Request to resume. Emit it synchronously from a user-gesture handler (a click or key press):
   * the loop's listener requests pointer lock inside that same call stack.
   */
  resume: undefined
  /** Settings changed (after `ctx.settings` was mutated). */
  settings: Settings
  /** Quality preset changed (after `ctx.quality` was updated and the render modules were told). */
  quality: QualityPreset
  /** The first user gesture ("Click to fly"): audio may start. Emitted once. */
  firstGesture: undefined
  /** M4 (plan I2): the Reach caught up the time the player was away (after a load or a long hidden tab). */
  awayReport: AwayReport
}

export type GameBusEvent = keyof GameBusEvents
export type GameBusHandler<K extends GameBusEvent> = (payload: GameBusEvents[K]) => void

/** A tiny typed emitter. Events are rare UI moments, never per frame. */
export interface GameBus {
  /** Subscribes; returns the unsubscribe function. Handlers run in subscription order. */
  on<K extends GameBusEvent>(type: K, handler: GameBusHandler<K>): () => void
  emit<K extends GameBusEvent>(type: K, payload: GameBusEvents[K]): void
}

export function createGameBus(): GameBus {
  const handlers: { [K in GameBusEvent]: GameBusHandler<K>[] } = {
    pause: [],
    resume: [],
    settings: [],
    quality: [],
    firstGesture: [],
    awayReport: [],
  }
  return {
    on<K extends GameBusEvent>(type: K, handler: GameBusHandler<K>): () => void {
      const list = handlers[type] as GameBusHandler<K>[]
      list.push(handler)
      return (): void => {
        const i = list.indexOf(handler)
        if (i >= 0) list.splice(i, 1)
      }
    },
    emit<K extends GameBusEvent>(type: K, payload: GameBusEvents[K]): void {
      const list = handlers[type] as GameBusHandler<K>[]
      // Iterate a snapshot length so a handler that unsubscribes itself does not skip its neighbour.
      const snapshot = list.slice()
      for (let i = 0; i < snapshot.length; i++) snapshot[i](payload)
    },
  }
}

/**
 * Additive (M3 R1, plan §2.5, D-247 11): modal UI over the running sim (maps, station panels, dialogs).
 * Helpers in src/app/ui.ts (`enterModal`, `consumeEscape`, `applyControlFilters`).
 */
export interface UiState {
  /** > 0 while a map or panel is open: flight input is zeroed and pointer-lock loss does not pause. */
  modal: number
  /** Esc handlers, a stack: the loop offers Esc to the last one first; a handler returning true consumes it. */
  readonly escape: (() => boolean)[]
  /** Requests pointer lock again, from inside the user gesture that closed the modal. */
  relock(): void
}

/** Additive (M3 R1): edits the player's control after it is read, per tick. Allocation-free; runs in registration order. */
export type ControlFilter = (c: PlayerControl) => void

/** Everything a feature may use. Created once at boot. */
export interface GameContext {
  readonly data: GameData
  readonly assets: AssetsFile
  readonly sim: Sim
  readonly renderer: WebGPURenderer
  readonly backend: 'webgpu' | 'webgl2'
  readonly canvas: HTMLCanvasElement
  /** The three.js scene. The camera sits at the render origin (D-011); objects are placed relative to `rig.pos`. */
  readonly scene: Scene
  readonly camera: PerspectiveCamera
  /** Float64 camera pose; `rig.pos` is the render origin in world space. */
  readonly rig: CameraRig
  readonly lib: MaterialLibrary
  readonly env: Environment
  readonly post: PostPipeline
  /** Every model in assets.json, by asset id. */
  readonly models: ReadonlyMap<string, LoadedModel>
  /** Every texture set in assets.json, by asset id. */
  readonly textureSets: ReadonlyMap<string, LoadedTextureSet>
  /** One view per ship entity (pose, faction look, engines, hits). */
  readonly views: ShipViews
  readonly input: InputDevices
  /** `#hud`: the in-flight overlay layer (pointer-events: none). */
  readonly hudRoot: HTMLElement
  /** `#overlay`: the modal layer (start, pause, errors). */
  readonly overlayRoot: HTMLElement
  readonly bus: GameBus
  settings: Settings
  quality: QualityPreset
  /** The game mode, fixed at boot. */
  readonly mode: GameMode
  /**
   * Hold reasons (e.g. 'docked', 'loading'). While any is present the loop runs no sim ticks and no `afterTick`,
   * without the pause menu: it releases pointer lock once, ignores pointer-lock loss, and does not re-lock on
   * resume; the Esc/P pause edge still opens the pause menu. When the last hold is removed the loop requests the
   * pointer lock again (remove it inside the user gesture that ends it, e.g. the Launch click; a refused lock
   * pauses with reason 'pointerLock'). Add and delete reasons between frames or from `beforeTicks`/`frame`.
   */
  readonly holds: Set<string>
  /** Typed service registry (`ServiceMap`): features provide and read shared runtimes (M2: `m2`). */
  readonly services: Services
  /** The asset loader boot used; features load deferred assets (`app.json deferredAssetPrefixes`) through it. */
  readonly loader: AssetLoader
  /**
   * Set by a feature's `init` to replace the "Click to fly" start overlay (M2: the title screen). Boot calls it
   * once after every `init` with `begin`, which must be called from a user gesture to start the game.
   */
  startOverride: ((begin: () => void) => void) | null
  /**
   * Additive (M3 R1): the Reach runtime in reach mode, and in career mode once `app.json careerInReach` is on
   * (R22d); null in the sandbox. Reach features gate on this, not on `mode`.
   */
  readonly reach: ReachRuntime | null
  /** Additive (M3 R1): modal UI state (maps and panels over the running sim). */
  readonly ui: UiState
  /** Additive (M3 R1): player-control filters, applied per tick after the control is read. */
  readonly controlFilters: ControlFilter[]
}

/** Per-frame information handed to `GameFeature.frame`. */
export interface FrameInfo {
  /** Real seconds since the last frame, clamped to `MAX_FRAME_SECONDS`. Still advances while paused. */
  dt: number
  /** Interpolation factor between the previous and current tick (1 while paused). */
  alpha: number
  /** `requestAnimationFrame` timestamp in milliseconds. */
  now: number
  paused: boolean
  /** A hold is active (`GameContext.holds`): no ticks ran this frame. The loop always sets it; absent = false. */
  held?: boolean
}

export interface GameFeature {
  /** Unique, used in error messages. */
  readonly name: string
  /** Frame order (FEATURE_ORDER); ties run by name. */
  readonly order: number
  /** Runs once at boot, sequentially in `order`, after the scene, sim and views exist. */
  init(ctx: GameContext): void | Promise<void>
  /**
   * Once per frame when not paused (also while held), after the pause, FA and camera edges and before the M1
   * combat edges (weapon select, cycle, target) and the ticks, so a feature can consume those edges first (M2 tool
   * modes). `f` carries this frame's `dt`, `now`, `paused` and `held` (`alpha` is last frame's). Allocation-free.
   */
  beforeTicks?(ctx: GameContext, f: FrameInfo): void
  /** After every sim tick (not while paused or held). Allocation-free. */
  afterTick?(ctx: GameContext): void
  /** Once per rendered frame, after the ship views and before `post.render()`. Allocation-free. */
  frame?(ctx: GameContext, f: FrameInfo): void
  /** Additive (M3 R1): once per frame after `post.render()`, for full-screen overlays (the maps). Allocation-free. */
  overlay?(ctx: GameContext, f: FrameInfo): void
  dispose?(): void
}

export const FEATURE_ORDER = Object.freeze({
  camera: 100,
  /** M2: the career runtime (provides the `m2` service; title screen, deferred station and prop assets). */
  career: 150,
  field: 200,
  /** M2: stations, wrecks, parts, chunks and fragments. */
  spaceViews: 250,
  fx: 400,
  /** M2: beams, guides and M2 particle effects. */
  spaceFx: 410,
  hud: 500,
  /** M2: the tool, scanner, salvage and docking HUD. */
  m2Hud: 510,
  audio: 600,
  /** M2: audio routing of the space events and readouts. */
  m2Audio: 610,
  menu: 700,
  /** M2: the station screens (React). */
  station: 710,
  dev: 900,
  // Additive (M3 R1, plan §2.5): numbers clear of the M2 plan's.
  reach: 160,
  structures: 210,
  scaled: 260,
  reachFx: 420,
  reachHud: 520,
  stationServices: 540,
  reachAudio: 620,
  maps: 750,
  /** M4 (plan I2): the strategic link's clock gate (after M3's `reach`). */
  strategic: 170,
  /** M4 (plan I6): the Reach Wire ticker and panel, the Station panel and the away report (after M3's `reachHud` 520). */
  wire: 525,
  /** M6 (plan P35): the fleet feed and the asset alert actions (after the Reach HUD's toasts 520; 527 and 528 are the Fleet and Captains screens, wire + 2 and + 3). */
  fleetFeed: 529,
  /** M5 (plan C18): the Charter HUD (jurisdiction chip, bounty toasts, scan meter, zones, gate, resonance). */
  charterHud: 530,
  /** M6 (plan P31): the wing radial menu (Y / D-pad ↑ hold) and the wing status pips (after the Charter HUD 530). */
  wingHud: 535,
  /** M7 (plan S35): the mission HUD: the objective line, story markers and the Leads map layer (after the wing HUD 535). */
  storyHud: 537,
  /** M7 (plan S31): the text comms panel and the waiting-hail prompt (after the wing HUD 535 and station services 540). */
  comms: 545,
  /** M5 (plan C19): the Charter's music cues (after M2's `m2Audio` 610 and M3's `reachAudio` 620). */
  charterAudio: 630,
})
